import { useState } from 'react';
import { Link, Route, Routes, useNavigate, useParams } from 'react-router-dom';
import {
  DISPOSITIVOS_SIN_CARRIL,
  SENTIDOS_CARRIL,
  TIPOS_DISPOSITIVO,
  type TipoDispositivo,
} from '@kodica/shared';

import { Iconos } from '../componentes/iconos';
import {
  Boton,
  Campo,
  Cargando,
  Chip,
  Encabezado,
  Entrada,
  ErrorCaja,
  Modal,
  Panel,
  Seleccion,
  Vacio,
} from '../componentes/ui';
import { ErrorApi, pedir } from '../lib/api';
import { copiarTexto } from '../lib/portapapeles';
import { Eventos, Probar } from './diagnostico';
import { useRecurso } from '../lib/consultas';
import { useQueSeSabeDeLosAparatos } from '../lib/enlace';

/**
 * Los aparatos del fraccionamiento, recorridos **como están cableados**.
 *
 *     Accesos  →  Carriles del acceso  →  Aparatos del carril  →  Un aparato
 *
 * Los aparatos se dan de alta y de baja **en el nivel del carril**, con el
 * carril ya fijado por el camino recorrido. No se vuelve a elegir en un
 * desplegable: de ahí salen los aparatos colgados del carril equivocado, y ese
 * error no falla ruidosamente —el mensaje se publica en una rama del árbol que
 * nadie escucha y la pluma simplemente no abre—.
 *
 * Son **rutas y no pestañas**: quien instala tiene el teléfono en la mano en la
 * caseta, necesita el botón de atrás del navegador y poder mandarle el enlace de
 * un aparato a alguien.
 *
 * Hasta ahora esta pantalla solo miraba. Dar de alta un aparato exigía entrar a
 * la base de datos, que es lo que convierte instalar una entrada nueva en una
 * tarea de quien escribió el sistema.
 */

interface Carril {
  id: string;
  nombre: string;
  slug: string;
  sentido: string;
  uso?: string;
  pulsoMs: number;
  activo: boolean;
}

interface PuntoAcceso {
  id: string;
  nombre: string;
  slug: string;
  tipo: string;
  /** Un acceso de baja se sigue enseñando, con su chip: si desapareciera de la
      lista no habría desde dónde volver a encenderlo. */
  activo: boolean;
  atendido: boolean;
  carriles: Carril[];
}

interface Dispositivo {
  id: string;
  nombre: string;
  tipo: string;
  clienteId: string;
  vivo: boolean;
  activo: boolean;
  ip: string | null;
  mac: string | null;
  modelo: string | null;
  firmware: string | null;
  ubicacion: string | null;
  ultimoLatido: string | null;
  tienePassword: boolean;
  usuarioAparato: string | null;
  puntoAcceso: { id: string; nombre: string };
  carril: { id: string; nombre: string } | null;
  topicos: Record<string, string> | null;
  configuracionHttp: Record<string, string> | null;
  /**
   * La cuenta con la que **el aparato entra al API**, si tiene.
   *
   * No confundir con `usuarioAparato`, que es al revés: esas son las
   * credenciales con las que el servidor entra **al aparato** —a la cámara, por
   * ISAPI—. Esta es la del kiosco llamando a Kodica.
   */
  cuentaServicio: {
    id: string;
    telefono: string | null;
    activo: boolean;
    ultimoAcceso: string | null;
  } | null;
}

/** El segmento del carril cuando el aparato sirve al acceso entero, como en el
 *  tópico MQTT: `kodica/{condominio}/{punto}/_/kiosco/...`. */
const SIN_CARRIL = '_';

export function TopologiaPagina() {
  return (
    <Routes>
      <Route index element={<Accesos />} />
      <Route path=":accesoId" element={<Carriles />} />
      <Route path=":accesoId/:carrilId" element={<Aparatos />} />
      <Route path=":accesoId/:carrilId/:aparatoId" element={<Ficha />} />
    </Routes>
  );
}

// ── Nivel 1: los accesos ──────────────────────────────────────

function Accesos() {
  const { sabeLatidos } = useQueSeSabeDeLosAparatos();
  const puntos = useRecurso<PuntoAcceso[]>('/topologia/puntos-acceso');
  const aparatos = useRecurso<Dispositivo[]>('/topologia/dispositivos');
  const [alta, setAlta] = useState(false);
  /**
   * **Lo dado de baja no se enseña.** Una lista de configuración es para operar
   * lo que existe, y un renglón tachado en medio obliga a leerlo cada vez para
   * descartarlo.
   *
   * Pero tampoco desaparece del todo, y eso es deliberado: si no hubiera forma
   * de verlo, no habría forma de reactivarlo, y dar de baja volvería a ser una
   * puerta de un solo sentido. El interruptor aparece **solo cuando hay algo
   * que enseñar** — sin nada de baja, ni siquiera se ve.
   */
  const [verBajas, setVerBajas] = useState(false);

  if (puntos.cargando) return <Cargando />;
  if (puntos.error) return <ErrorCaja>{puntos.error}</ErrorCaja>;

  const todos = aparatos.datos ?? [];

  /**
   * Da de baja un acceso. No se borra: de él cuelga la bitácora de todo lo que
   * pasó por esa reja, y el servidor lo desactiva en vez de eliminarlo.
   *
   * El servidor también decide cuándo no se puede —le quedan carriles activos,
   * o es el último del condominio— y aquí solo se enseña lo que respondió. La
   * comprobación no se repite en el navegador: duplicarla la deja envejecer por
   * su cuenta y acaba diciendo que sí a algo que el servidor rechaza.
   */
  async function cambiarBajaPunto(p: PuntoAcceso) {
    if (p.activo) {
      if (
        !window.confirm(
          `¿Dar de baja «${p.nombre}»? Deja de recibir visitas, pero su historial se conserva y lo puedes reactivar después.`,
        )
      )
        return;
    }
    try {
      if (p.activo) {
        await pedir(`/topologia/puntos-acceso/${p.id}`, { metodo: 'DELETE' });
      } else {
        await pedir(`/topologia/puntos-acceso/${p.id}`, {
          metodo: 'PATCH',
          cuerpo: { activo: true },
        });
      }
      puntos.recargar();
    } catch (e) {
      window.alert(e instanceof ErrorApi ? e.message : 'No se pudo cambiar');
    }
  }

  return (
    <>
      <Encabezado
        titulo="Accesos y aparatos"
        descripcion="Entra a un acceso para ver sus carriles y el equipo de cada uno"
      >
        <Boton variante="primario" icono={Iconos.agregar} onClick={() => setAlta(true)}>
          Agregar acceso
        </Boton>
      </Encabezado>

      <ModalAcceso
        abierto={alta}
        alCerrar={() => setAlta(false)}
        alGuardar={() => {
          setAlta(false);
          puntos.recargar();
        }}
      />

      <InterruptorBajas
        cuantas={(puntos.datos ?? []).filter((p) => !p.activo).length}
        puesto={verBajas}
        alCambiar={setVerBajas}
        que="accesos"
      />

      <div className="grid gap-gutter xl:grid-cols-2">
        {(puntos.datos ?? [])
          .filter((p) => verBajas || p.activo)
          .map((p) => {
            const suyos = todos.filter((d) => d.puntoAcceso.id === p.id && d.activo);
            // La nube no recibe latidos —no habla MQTT con los aparatos— asi que
            // aqui `vivo` es siempre falso. Contarlos como caidos pondria una
            // alarma permanente y falsa sobre equipos que estan funcionando.
            const caidos = sabeLatidos ? suyos.filter((d) => !d.vivo).length : 0;
            return (
              <Link key={p.id} to={p.id} className="block">
                <Panel>
                  <div className="flex items-start justify-between gap-3 px-6 py-5">
                    <div className="min-w-0">
                      <h3 className="text-headline-md text-on-surface">{p.nombre}</h3>
                      {/* El slug se enseña porque es el segmento del tópico de
                        todos sus aparatos, y no se puede cambiar después. */}
                      <p className="mt-0.5 font-mono text-label-sm text-on-surface-variant">
                        {p.slug}
                      </p>
                      {/* Lo que hace falta para decidir si entrar, sin entrar. */}
                      <p className="mt-2 text-body-md text-on-surface-variant">
                        {p.carriles.length} {p.carriles.length === 1 ? 'carril' : 'carriles'} ·{' '}
                        {suyos.length} {suyos.length === 1 ? 'aparato' : 'aparatos'}
                      </p>
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-2">
                      <Chip tono={p.atendido ? 'activo' : 'neutro'}>
                        {p.atendido ? 'Con caseta' : 'Desatendido'}
                      </Chip>
                      {caidos > 0 && <Chip tono="malo">{caidos} sin latido</Chip>}
                      {/* **Que se vea que está de baja.** Sin este chip, un acceso
                        apagado se veía idéntico a uno operando: se pulsaba «dar
                        de baja» otra vez y el servidor contestaba «es el único
                        acceso», que no tiene sentido delante de dos tarjetas. */}
                      {!p.activo && <Chip tono="neutro">de baja</Chip>}
                      {/* Va dentro del enlace, así que hay que frenar la
                        navegación: sin esto, dar de baja abriría además la
                        ficha del acceso que se acaba de quitar. */}
                      <Boton
                        variante="texto"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          void cambiarBajaPunto(p);
                        }}
                      >
                        {p.activo ? 'Dar de baja' : 'Reactivar'}
                      </Boton>
                    </div>
                  </div>
                </Panel>
              </Link>
            );
          })}
      </div>

      {(puntos.datos ?? []).filter((p) => verBajas || p.activo).length === 0 && (
        <Panel>
          <Vacio>Todavía no hay accesos dados de alta.</Vacio>
        </Panel>
      )}
    </>
  );
}

// ── Nivel 2: los carriles del acceso ──────────────────────────

function Carriles() {
  const { sabeLatidos } = useQueSeSabeDeLosAparatos();
  const { accesoId = '' } = useParams();
  const puntos = useRecurso<PuntoAcceso[]>('/topologia/puntos-acceso');
  const aparatos = useRecurso<Dispositivo[]>('/topologia/dispositivos');
  const [alta, setAlta] = useState(false);
  const [verBajas, setVerBajas] = useState(false);

  if (puntos.cargando) return <Cargando />;
  if (puntos.error) return <ErrorCaja>{puntos.error}</ErrorCaja>;

  const punto = (puntos.datos ?? []).find((p) => p.id === accesoId);
  if (!punto) return <Vacio>No se encontró el acceso.</Vacio>;

  const todos = (aparatos.datos ?? []).filter((d) => d.puntoAcceso.id === punto.id && d.activo);
  // Los que sirven al acceso entero y no a una pluma: el kiosco y el teléfono.
  const delAcceso = todos.filter((d) => !d.carril);

  /**
   * Da de baja un carril. Tampoco se borra: de él cuelgan las aperturas de su
   * pluma, que son la evidencia de quién entró por dónde.
   *
   * Si todavía tiene aparatos, el servidor se niega y lo explica. No se
   * comprueba aquí también: dos guardas que dicen lo mismo terminan
   * discrepando, y la que manda es la del servidor.
   */
  async function cambiarBajaCarril(c: Carril) {
    if (c.activo) {
      if (
        !window.confirm(
          `¿Dar de baja el carril «${c.nombre}»? Deja de operar, pero su historial se conserva y lo puedes reactivar después.`,
        )
      )
        return;
    }
    try {
      if (c.activo) {
        await pedir(`/topologia/carriles/${c.id}`, { metodo: 'DELETE' });
      } else {
        await pedir(`/topologia/carriles/${c.id}`, { metodo: 'PATCH', cuerpo: { activo: true } });
      }
      puntos.recargar();
    } catch (e) {
      window.alert(e instanceof ErrorApi ? e.message : 'No se pudo cambiar');
    }
  }

  return (
    <>
      <Volver a="/topologia">Accesos</Volver>
      <Encabezado titulo={punto.nombre} descripcion={`${punto.slug} · ${punto.tipo.toLowerCase()}`}>
        <Chip tono={punto.atendido ? 'activo' : 'neutro'}>
          {punto.atendido ? 'Con caseta' : 'Desatendido'}
        </Chip>
        <Boton variante="primario" icono={Iconos.agregar} onClick={() => setAlta(true)}>
          Agregar carril
        </Boton>
      </Encabezado>

      <ModalCarril
        abierto={alta}
        puntoAccesoId={punto.id}
        alCerrar={() => setAlta(false)}
        alGuardar={() => {
          setAlta(false);
          puntos.recargar();
        }}
      />

      <InterruptorBajas
        cuantas={punto.carriles.filter((c) => !c.activo).length}
        puesto={verBajas}
        alCambiar={setVerBajas}
        que="carriles"
      />

      <Seccion titulo="Carriles">
        {punto.carriles.filter((c) => verBajas || c.activo).length === 0 ? (
          <Vacio>
            Sin carriles. Cada entrada y cada salida es un carril, y de él cuelga su pluma.
          </Vacio>
        ) : (
          <ul className="divide-y divide-outline-variant">
            {punto.carriles
              .filter((c) => verBajas || c.activo)
              .map((c) => {
                const suyos = todos.filter((d) => d.carril?.id === c.id);
                const caidos = sabeLatidos ? suyos.filter((d) => !d.vivo).length : 0;
                return (
                  <li key={c.id}>
                    <Link
                      to={c.id}
                      className="toque flex items-center justify-between gap-4 px-6 py-4 transition-colors hover:bg-surface-container-high"
                    >
                      <div className="min-w-0">
                        <p className="truncate text-body-md text-on-surface">{c.nombre}</p>
                        <p className="truncate font-mono text-label-sm text-on-surface-variant">
                          {c.slug} · {c.sentido.toLowerCase()} ·{' '}
                          {c.uso === 'CONDOMINOS' ? 'solo condóminos' : 'visitas'} · {c.pulsoMs} ms
                        </p>
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        <span className="text-label-sm text-on-surface-variant">
                          {suyos.length} {suyos.length === 1 ? 'aparato' : 'aparatos'}
                        </span>
                        {caidos > 0 && <Chip tono="malo">{caidos} sin latido</Chip>}
                        {!c.activo && <Chip tono="neutro">inactivo</Chip>}
                        <Boton
                          variante="texto"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            void cambiarBajaCarril(c);
                          }}
                        >
                          {c.activo ? 'Dar de baja' : 'Reactivar'}
                        </Boton>
                        <Iconos.siguiente tam={20} />
                      </div>
                    </Link>
                  </li>
                );
              })}
          </ul>
        )}
      </Seccion>

      {/* Aparte, porque no cuelgan de ninguna pluma: el kiosco atiende a quien
          llega a pie al acceso y el teléfono es de la caseta. */}
      <div className="mt-stack-md">
        <Seccion titulo="Aparatos del acceso, sin carril">
          <ListaAparatos
            aparatos={delAcceso}
            enlace={(d) => `${SIN_CARRIL}/${d.id}`}
            vacio="Ninguno. El kiosco y el teléfono de caseta van aquí."
          />
        </Seccion>
      </div>
    </>
  );
}

// ── Nivel 3: los aparatos del carril ──────────────────────────

function Aparatos() {
  const { accesoId = '', carrilId = '' } = useParams();
  const navegar = useNavigate();
  const puntos = useRecurso<PuntoAcceso[]>('/topologia/puntos-acceso');
  const { datos, cargando, error, recargar } = useRecurso<Dispositivo[]>('/topologia/dispositivos');
  const [alta, setAlta] = useState(false);
  const [verBajas, setVerBajas] = useState(false);

  const punto = (puntos.datos ?? []).find((p) => p.id === accesoId);
  const carril = punto?.carriles.find((c) => c.id === carrilId);
  const sinCarril = carrilId === SIN_CARRIL;

  if (cargando || puntos.cargando) return <Cargando />;
  if (error) return <ErrorCaja>{error}</ErrorCaja>;
  if (!punto) return <Vacio>No se encontró el acceso.</Vacio>;
  if (!carril && !sinCarril) return <Vacio>No se encontró el carril.</Vacio>;

  // Los de este carril, activos o no. El filtro por `activo` va después, para
  // poder contar cuántos hay de baja y ofrecer verlos.
  const delCarril = (datos ?? []).filter(
    (d) => d.puntoAcceso.id === accesoId && (sinCarril ? !d.carril : d.carril?.id === carrilId),
  );
  const suyos = delCarril.filter((d) => verBajas || d.activo);

  return (
    <>
      <Volver a={`/topologia/${accesoId}`}>{punto.nombre}</Volver>
      <Encabezado
        titulo={carril ? carril.nombre : 'Aparatos del acceso'}
        descripcion={
          carril
            ? `${punto.nombre} · ${carril.slug} · ${carril.sentido.toLowerCase()}`
            : `${punto.nombre} · aparatos que sirven al acceso entero`
        }
      >
        <Boton variante="primario" icono={Iconos.agregar} onClick={() => setAlta(true)}>
          Agregar
        </Boton>
      </Encabezado>

      <InterruptorBajas
        cuantas={delCarril.filter((d) => !d.activo).length}
        puesto={verBajas}
        alCambiar={setVerBajas}
        que="aparatos"
      />

      <Panel>
        <ListaAparatos
          aparatos={suyos}
          enlace={(d) => d.id}
          vacio={
            carril
              ? 'Este carril no tiene aparatos. Sin controlador de pluma, no abre.'
              : 'Ninguno todavía.'
          }
        />
      </Panel>

      <ModalAparato
        abierto={alta}
        carrilId={sinCarril ? null : carrilId}
        registro={null}
        alCerrar={() => setAlta(false)}
        alGuardar={(creado) => {
          setAlta(false);
          recargar();
          // Se entra derecho a su ficha: ahí está lo que hay que copiarle al
          // equipo, y este es el momento en que alguien lo tiene delante para
          // configurarlo. Enseñárselo después, si se acuerda de buscarlo, es
          // cómo se consigue que el tópico se teclee a mano desde la
          // documentación —y se equivoque en un guion—.
          if (creado) navegar(`/topologia/${accesoId}/${carrilId}/${creado.id}`);
        }}
      />
    </>
  );
}

/**
 * La cuenta con la que este aparato entra al API.
 *
 * ## Por qué vive aquí y no en la pantalla de personas
 *
 * Porque se ve igual que la de un guardia —mismo rol, un nombre— y no se
 * comporta igual: **nadie la teclea**, vive en un archivo del equipo, y
 * cambiarla sin ir a ponérsela también al aparato lo deja fuera sin que nada
 * avise.
 *
 * Pasó tal cual: se cambiaron tres contraseñas desde el portal y el kiosco de
 * la reja dejó de poder validar QR. No se cayó ni protestó — se quedó
 * reintentando, que es peor, porque nadie se entera hasta que un visitante está
 * parado en la puerta.
 *
 * Poniéndola desde aquí, quien la cambia está mirando el equipo al que se la
 * tiene que copiar.
 */
function CuentaDeServicio({ d, alCambiar }: { d: Dispositivo; alCambiar: () => void }) {
  const cuenta = d.cuentaServicio!;
  const [nueva, setNueva] = useState('');
  const [repetida, setRepetida] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [listo, setListo] = useState(false);
  const [guardando, setGuardando] = useState(false);

  async function enviar(evento: React.FormEvent) {
    evento.preventDefault();
    setError(null);
    setListo(false);

    if (nueva !== repetida) {
      setError('Las dos contraseñas no son iguales.');
      return;
    }

    setGuardando(true);
    try {
      await pedir(`/topologia/dispositivos/${d.id}/password-servicio`, {
        metodo: 'POST',
        cuerpo: { password: nueva },
      });
      setNueva('');
      setRepetida('');
      setListo(true);
      alCambiar();
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message : 'No se pudo cambiar.');
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Seccion titulo="Cómo entra este aparato a Kodica">
      <p className="mb-stack-md text-body-md text-on-surface-variant">
        Con esta cuenta el aparato llama al API. No es de una persona: no aparece en la lista de
        personal y nadie la teclea a mano.
      </p>

      <dl className="mb-stack-md divide-y divide-outline-variant">
        <Dato etiqueta="Entra como" valor={cuenta.telefono} mono />
        <Dato
          etiqueta="Última vez que entró"
          valor={cuenta.ultimoAcceso ? new Date(cuenta.ultimoAcceso).toLocaleString('es-MX') : null}
          ayuda={cuenta.ultimoAcceso ? undefined : 'Nunca. Revisa la contraseña del equipo.'}
        />
      </dl>

      <form onSubmit={enviar} className="space-y-4">
        <Campo etiqueta="Contraseña nueva" ayuda="Al menos 10 caracteres.">
          <Entrada
            type="password"
            autoComplete="new-password"
            minLength={10}
            required
            value={nueva}
            onChange={(e) => setNueva(e.target.value)}
          />
        </Campo>
        <Campo etiqueta="Repítela">
          <Entrada
            type="password"
            autoComplete="new-password"
            required
            value={repetida}
            onChange={(e) => setRepetida(e.target.value)}
          />
        </Campo>

        {error && <ErrorCaja>{error}</ErrorCaja>}

        {listo && (
          <div className="rounded-lg bg-tertiary-container/20 p-3 text-body-md text-tertiary">
            <strong>Cambiada aquí. Falta el equipo.</strong> Ponle la misma contraseña al aparato y
            reinícialo, o dejará de conectarse — sin avisar.
          </div>
        )}

        <Boton type="submit" variante="contorno" disabled={guardando} className="w-full">
          {guardando ? 'Guardando…' : 'Cambiar la contraseña del aparato'}
        </Boton>
      </form>
    </Seccion>
  );
}

// ── Nivel 4: la ficha de un aparato ───────────────────────────

function Ficha() {
  const { accesoId = '', carrilId = '', aparatoId = '' } = useParams();
  const navegar = useNavigate();
  const { datos, cargando, error, recargar } = useRecurso<Dispositivo[]>('/topologia/dispositivos');
  const [editando, setEditando] = useState(false);

  if (cargando) return <Cargando />;
  if (error) return <ErrorCaja>{error}</ErrorCaja>;

  const d = (datos ?? []).find((x) => x.id === aparatoId);
  if (!d) return <Vacio>No se encontró el aparato.</Vacio>;

  async function darDeBaja() {
    if (!window.confirm('¿Dar de baja este aparato? Deja de contar, pero no se borra.')) return;
    try {
      await pedir(`/topologia/dispositivos/${aparatoId}`, { metodo: 'DELETE' });
      navegar(`/topologia/${accesoId}/${carrilId}`);
    } catch (e) {
      window.alert(e instanceof ErrorApi ? e.message : 'No se pudo dar de baja');
    }
  }

  /**
   * Vuelve a ponerlo en servicio.
   *
   * No navega hacia atrás como la baja: quien reactiva un aparato normalmente
   * viene a seguir configurándolo —copiarle los tópicos, revisarle la IP— y
   * echarlo de la ficha lo obligaría a volver a entrar.
   */
  async function reactivar() {
    try {
      await pedir(`/topologia/dispositivos/${aparatoId}`, {
        metodo: 'PATCH',
        cuerpo: { activo: true },
      });
      recargar();
    } catch (e) {
      window.alert(e instanceof ErrorApi ? e.message : 'No se pudo reactivar');
    }
  }

  return (
    <>
      <Volver a={`/topologia/${accesoId}/${carrilId}`}>
        {d.carril ? d.carril.nombre : 'Aparatos del acceso'}
      </Volver>
      <Encabezado titulo={d.nombre} descripcion={d.tipo.replace(/_/g, ' ').toLowerCase()}>
        {/* Un aparato de baja no está «sin latido»: está apagado a propósito, y
            decirle lo otro manda a alguien a buscar una avería que no existe. */}
        {d.activo ? (
          <Chip tono={d.vivo ? 'ok' : 'malo'}>{d.vivo ? 'En línea' : 'Sin latido'}</Chip>
        ) : (
          <Chip tono="neutro">De baja</Chip>
        )}
        <Boton variante="texto" onClick={() => setEditando(true)}>
          Editar
        </Boton>
        {d.activo ? (
          <Boton variante="texto" onClick={() => void darDeBaja()}>
            Dar de baja
          </Boton>
        ) : (
          <Boton variante="texto" onClick={() => void reactivar()}>
            Reactivar
          </Boton>
        )}
      </Encabezado>

      <div className="grid gap-stack-md lg:grid-cols-2">
        <Seccion titulo="El aparato">
          <dl className="divide-y divide-outline-variant">
            <Dato etiqueta="Identificador MQTT" valor={d.clienteId} mono />
            <Dato etiqueta="IP" valor={d.ip} mono />
            <Dato etiqueta="MAC" valor={d.mac} mono />
            <Dato etiqueta="Modelo" valor={d.modelo} />
            <Dato etiqueta="Firmware" valor={d.firmware} />
            <Dato etiqueta="Ubicación" valor={d.ubicacion} />
            <Dato etiqueta="Usuario del aparato" valor={d.usuarioAparato} />
            <Dato
              etiqueta="Contraseña"
              valor={d.tienePassword ? 'guardada' : null}
              ayuda="No se puede leer, solo reemplazar"
            />
            <Dato
              etiqueta="Último latido"
              valor={d.ultimoLatido ? new Date(d.ultimoLatido).toLocaleString('es-MX') : null}
            />
          </dl>
        </Seccion>

        <ComoSeConfigura d={d} />
      </div>

      {d.cuentaServicio && (
        <div className="mt-stack-md">
          <CuentaDeServicio d={d} alCambiar={recargar} />
        </div>
      )}

      <div className="mt-stack-md grid gap-stack-md lg:grid-cols-2">
        <Probar d={d} />
        <Eventos dispositivoId={d.id} />
      </div>

      <ModalAparato
        abierto={editando}
        carrilId={d.carril?.id ?? null}
        registro={d}
        alCerrar={() => setEditando(false)}
        alGuardar={() => {
          setEditando(false);
          recargar();
        }}
      />
    </>
  );
}

/**
 * Lo que hay que copiarle al equipo para que hable con Kodica.
 *
 * Teclearlo a mano desde `docs/05` es la forma más fácil de que una semana se
 * vaya en depurar por qué un mensaje no llega: un tópico mal escrito no falla
 * ruidosamente, se publica en una rama que nadie escucha.
 *
 * Y **no todos van por el bus**: la terminal facial empuja por HTTP y el
 * teléfono habla SIP contra Asterisk. Cada uno enseña lo suyo, y el que no tiene
 * nada que copiar lo dice, en vez de dejar un hueco que parezca un error.
 */
function ComoSeConfigura({ d }: { d: Dispositivo }) {
  const [copiado, setCopiado] = useState<string | null>(null);
  const [fallo, setFallo] = useState(false);

  async function copiar(clave: string, valor: string) {
    // Se enseña el fallo, no se calla. Un botón que dice «copiado» sin haber
    // copiado hace que alguien pegue el tópico anterior en un aparato y pase la
    // tarde buscando por qué no le llegan los mensajes.
    if (await copiarTexto(valor)) {
      setFallo(false);
      setCopiado(clave);
      setTimeout(() => setCopiado(null), 1500);
    } else {
      setFallo(true);
    }
  }

  const lineas = d.topicos ?? d.configuracionHttp;

  return (
    <Seccion titulo="Cómo se configura">
      {!lineas ? (
        <p className="p-5 text-body-md text-on-surface-variant">
          Este aparato no se configura contra el bus. El teléfono de caseta habla SIP contra
          Asterisk; se configura en la pantalla de Telefonía.
        </p>
      ) : (
        <div className="p-5">
          <p className="mb-3 text-label-sm text-on-surface-variant">
            {d.topicos
              ? 'Estos tópicos van en el aparato. Publica en evento, estado y latido, y se suscribe a comando.'
              : 'Esta terminal empuja sus eventos por HTTP. Kodica se lo carga sola al arrancar; aquí está por si hay que comprobarlo o ponerlo a mano.'}
          </p>
          {fallo && (
            <p className="mb-3 text-label-sm text-error">
              Este navegador no deja copiar solo. Selecciona el texto y cópialo a mano.
            </p>
          )}
          <ul className="space-y-1">
            {Object.entries(lineas).map(([clave, valor]) => (
              <li key={clave} className="flex items-center gap-3">
                <span className="w-24 shrink-0 text-label-sm text-on-surface-variant">{clave}</span>
                {/* `select-all`: un toque selecciona el tópico entero, que es
                    lo que salva la situación si ni el respaldo funciona. */}
                <code className="min-w-0 flex-1 select-all truncate font-mono text-label-bold text-on-surface">
                  {valor}
                </code>
                <button
                  type="button"
                  onClick={() => void copiar(clave, valor)}
                  aria-label={`Copiar ${clave}`}
                  className="inline-flex shrink-0 items-center gap-1 rounded-lg px-2 py-1 text-label-sm text-primary transition-colors hover:bg-surface-container-high"
                >
                  {copiado === clave ? (
                    <>
                      <Iconos.correcto tam={16} /> copiado
                    </>
                  ) : (
                    <>
                      <Iconos.copiar tam={16} /> copiar
                    </>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Seccion>
  );
}

// ── Los formularios de la topología ───────────────────────────

/**
 * Un acceso nuevo. Es la reja o la puerta por la que se entra al
 * fraccionamiento; de él cuelgan sus carriles y su equipo.
 *
 * Hasta ahora esto solo se podía hacer en la base de datos, así que abrir una
 * entrada nueva era tarea de quien escribió el sistema. La API lo permitía desde
 * el principio; lo que faltaba era la pantalla.
 */
function ModalAcceso({
  abierto,
  alCerrar,
  alGuardar,
}: {
  abierto: boolean;
  alCerrar: () => void;
  alGuardar: () => void;
}) {
  const [campos, setCampos] = useState<Record<string, string>>({ tipo: 'PRINCIPAL' });
  const [atendido, setAtendido] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  async function enviar(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setEnviando(true);
    try {
      await pedir('/topologia/puntos-acceso', {
        metodo: 'POST',
        cuerpo: {
          nombre: campos['nombre'],
          tipo: campos['tipo'],
          atendido,
          ubicacion: campos['ubicacion'] || null,
        },
      });
      setCampos({ tipo: 'PRINCIPAL' });
      alGuardar();
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'No se pudo guardar');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Modal titulo="Nuevo acceso" abierto={abierto} alCerrar={alCerrar}>
      <form onSubmit={enviar} className="space-y-4">
        <Campo
          etiqueta="Nombre"
          ayuda="De él sale su identificador, que va dentro del tópico de todos sus aparatos"
        >
          <Entrada
            required
            placeholder="Acceso principal"
            value={campos['nombre'] ?? ''}
            onChange={(e) => setCampos((c) => ({ ...c, nombre: e.target.value }))}
          />
        </Campo>

        <Campo etiqueta="Tipo">
          <Seleccion
            value={campos['tipo'] ?? 'PRINCIPAL'}
            onChange={(e) => setCampos((c) => ({ ...c, tipo: e.target.value }))}
          >
            {['PRINCIPAL', 'SECUNDARIO', 'SERVICIO', 'PEATONAL', 'EMERGENCIA'].map((t) => (
              <option key={t} value={t}>
                {t.toLowerCase()}
              </option>
            ))}
          </Seleccion>
        </Campo>

        {/* No es cosmético: un acceso desatendido no tiene a quién mandarle las
            visitas del kiosco, así que solo abre por tag o por QR válido. */}
        <Campo etiqueta="¿Tiene caseta con guardia?">
          <Seleccion
            value={atendido ? 'si' : 'no'}
            onChange={(e) => setAtendido(e.target.value === 'si')}
          >
            <option value="si">Sí, hay guardia</option>
            <option value="no">No: desatendido, solo abre con tag o QR</option>
          </Seleccion>
        </Campo>

        <Campo etiqueta="Ubicación">
          <Entrada
            placeholder="Av. Principal, entrada norte"
            value={campos['ubicacion'] ?? ''}
            onChange={(e) => setCampos((c) => ({ ...c, ubicacion: e.target.value }))}
          />
        </Campo>

        {error && <ErrorCaja>{error}</ErrorCaja>}

        <div className="flex justify-end gap-2 pt-2">
          <Boton type="button" variante="texto" onClick={alCerrar}>
            Cancelar
          </Boton>
          <Boton type="submit" variante="primario" disabled={enviando}>
            {enviando ? 'Guardando…' : 'Guardar'}
          </Boton>
        </div>
      </form>
    </Modal>
  );
}

/**
 * Un carril nuevo: **cada entrada y cada salida es uno**, y de él cuelga su
 * pluma. Un acceso con entrada y salida son dos carriles, no uno de doble
 * sentido; el bidireccional es para la reja única que se usa en los dos
 * sentidos con una sola pluma.
 */
function ModalCarril({
  abierto,
  puntoAccesoId,
  alCerrar,
  alGuardar,
}: {
  abierto: boolean;
  puntoAccesoId: string;
  alCerrar: () => void;
  alGuardar: () => void;
}) {
  const [campos, setCampos] = useState<Record<string, string>>({
    sentido: 'ENTRADA',
    uso: 'VISITAS',
    pulsoMs: '800',
  });
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  function set(k: string, v: string) {
    setCampos((c) => ({ ...c, [k]: v }));
  }

  async function enviar(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setEnviando(true);
    try {
      await pedir(`/topologia/puntos-acceso/${puntoAccesoId}/carriles`, {
        metodo: 'POST',
        cuerpo: {
          nombre: campos['nombre'],
          sentido: campos['sentido'],
          uso: campos['uso'],
          pulsoMs: Number(campos['pulsoMs'] || 800),
        },
      });
      alGuardar();
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'No se pudo guardar');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Modal titulo="Nuevo carril" abierto={abierto} alCerrar={alCerrar}>
      <form onSubmit={enviar} className="space-y-4">
        <Campo etiqueta="Nombre" ayuda="Entrada 1, Salida, Visitas…">
          <Entrada
            required
            placeholder="Entrada 1"
            value={campos['nombre'] ?? ''}
            onChange={(e) => set('nombre', e.target.value)}
          />
        </Campo>

        <Campo
          etiqueta="Sentido"
          ayuda="Una entrada y una salida son dos carriles. El bidireccional es una reja única con una sola pluma."
        >
          <Seleccion
            value={campos['sentido'] ?? 'ENTRADA'}
            onChange={(e) => set('sentido', e.target.value)}
          >
            {SENTIDOS_CARRIL.map((s) => (
              <option key={s} value={s}>
                {s.toLowerCase()}
              </option>
            ))}
          </Seleccion>
        </Campo>

        {/* La asimetría es del primer día: por el de visitas también pasan los
            condóminos, pero por el de condóminos no pasa una visita. */}
        <Campo etiqueta="Para quién es">
          <Seleccion
            value={campos['uso'] ?? 'VISITAS'}
            onChange={(e) => set('uso', e.target.value)}
          >
            <option value="VISITAS">Visitas (también pasan condóminos)</option>
            <option value="CONDOMINOS">Solo condóminos, con tag o facial</option>
          </Seleccion>
        </Campo>

        <Campo
          etiqueta="Pulso de la pluma"
          ayuda="Milisegundos que dura la orden al relé. Una pluma lenta con 300 no alcanza a subir."
        >
          <Entrada
            type="number"
            min={100}
            max={10000}
            value={campos['pulsoMs'] ?? '800'}
            onChange={(e) => set('pulsoMs', e.target.value)}
          />
        </Campo>

        {error && <ErrorCaja>{error}</ErrorCaja>}

        <div className="flex justify-end gap-2 pt-2">
          <Boton type="button" variante="texto" onClick={alCerrar}>
            Cancelar
          </Boton>
          <Boton type="submit" variante="primario" disabled={enviando}>
            {enviando ? 'Guardando…' : 'Guardar'}
          </Boton>
        </div>
      </form>
    </Modal>
  );
}

// ── El formulario del aparato ─────────────────────────────────

function ModalAparato({
  abierto,
  carrilId,
  registro,
  alCerrar,
  alGuardar,
}: {
  abierto: boolean;
  /** Fijado por el camino recorrido. `null` = aparato del acceso entero. */
  carrilId: string | null;
  registro: Dispositivo | null;
  alCerrar: () => void;
  alGuardar: (creado?: Dispositivo) => void;
}) {
  const [campos, setCampos] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [listo, setListo] = useState(false);

  // Se rellena al abrir. Sin esto, editar un aparato traería los datos del
  // anterior que se hubiera mirado.
  if (abierto && !listo) {
    setListo(true);
    setCampos(
      registro
        ? {
            nombre: registro.nombre,
            tipo: registro.tipo,
            clienteId: registro.clienteId,
            modelo: registro.modelo ?? '',
            ip: registro.ip ?? '',
            mac: registro.mac ?? '',
            firmware: registro.firmware ?? '',
            ubicacion: registro.ubicacion ?? '',
            usuarioAparato: registro.usuarioAparato ?? '',
          }
        : { tipo: carrilId ? 'CONTROLADOR_PLUMA' : 'KIOSCO' },
    );
    setError(null);
  }
  if (!abierto && listo) setListo(false);

  function set(k: string, v: string) {
    setCampos((c) => ({ ...c, [k]: v }));
  }

  const tipo = (campos['tipo'] ?? 'CONTROLADOR_PLUMA') as TipoDispositivo;
  const esFacial = tipo === 'LECTOR_FACIAL';
  // Los dos aparatos a los que Kodica **les habla por IP** con digest: el
  // terminal facial para enrolar caras, y la cámara de rostro para pedirle la
  // foto del visitante. Los dos necesitan dirección fija y credenciales, y sin
  // ellas no sirven de nada — así que el formulario las exige igual.
  const hablaPorIsapi = esFacial || tipo === 'CAMARA_ROSTRO';
  // Los que sirven al acceso entero solo pueden darse de alta fuera de un
  // carril; lo demás tiene que colgar de uno, y el API lo rechaza igual.
  const tiposPosibles = carrilId
    ? TIPOS_DISPOSITIVO
    : TIPOS_DISPOSITIVO.filter((t) => DISPOSITIVOS_SIN_CARRIL.includes(t));

  async function enviar(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setEnviando(true);
    try {
      const cuerpo: Record<string, unknown> = Object.fromEntries(
        Object.entries(campos).filter(([, v]) => v !== ''),
      );
      // El tipo y el identificador no se cambian: el `clienteId` es el segmento
      // del tópico y cambiarlo desconecta un aparato ya instalado.
      if (registro) {
        delete cuerpo['tipo'];
        delete cuerpo['clienteId'];
      } else {
        cuerpo['carrilId'] = carrilId;
      }
      const guardado = await pedir<Dispositivo>(
        registro ? `/topologia/dispositivos/${registro.id}` : '/topologia/dispositivos',
        { metodo: registro ? 'PATCH' : 'POST', cuerpo },
      );
      alGuardar(guardado);
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'No se pudo guardar');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Modal
      titulo={registro ? 'Editar aparato' : 'Nuevo aparato'}
      abierto={abierto}
      alCerrar={alCerrar}
    >
      <form onSubmit={enviar} className="space-y-4">
        <Campo etiqueta="Nombre">
          <Entrada
            required
            placeholder="Controlador de pluma entrada 1"
            value={campos['nombre'] ?? ''}
            onChange={(e) => set('nombre', e.target.value)}
          />
        </Campo>

        <Campo etiqueta="Tipo" ayuda={registro ? 'No se puede cambiar' : undefined}>
          <Seleccion
            value={campos['tipo'] ?? ''}
            disabled={Boolean(registro)}
            onChange={(e) => set('tipo', e.target.value)}
          >
            {tiposPosibles.map((t) => (
              <option key={t} value={t}>
                {t.replace(/_/g, ' ').toLowerCase()}
              </option>
            ))}
          </Seleccion>
        </Campo>

        <Campo
          etiqueta="Identificador MQTT"
          ayuda={
            registro
              ? 'No se puede cambiar: es el segmento del tópico, y cambiarlo desconecta el aparato'
              : 'Minúsculas y guiones: plu-e1, uhf-e1. Va dentro de su tópico.'
          }
        >
          <Entrada
            required
            disabled={Boolean(registro)}
            placeholder="plu-e1"
            value={campos['clienteId'] ?? ''}
            onChange={(e) => set('clienteId', e.target.value)}
          />
        </Campo>

        <div className="grid gap-4 sm:grid-cols-2">
          <Campo
            etiqueta="IP"
            ayuda={
              hablaPorIsapi
                ? esFacial
                  ? 'Fija, y obligatoria: Kodica le habla para enrolar caras'
                  : 'Fija, y obligatoria: Kodica le pide la foto del visitante'
                : undefined
            }
          >
            <Entrada
              required={hablaPorIsapi}
              placeholder="192.168.68.95"
              value={campos['ip'] ?? ''}
              onChange={(e) => set('ip', e.target.value)}
            />
          </Campo>
          <Campo etiqueta="MAC" ayuda="Lo único que no cambia al reconfigurar la red">
            <Entrada
              placeholder="88:DE:39:66:F1:0D"
              value={campos['mac'] ?? ''}
              onChange={(e) => set('mac', e.target.value)}
            />
          </Campo>
          <Campo etiqueta="Modelo">
            <Entrada
              value={campos['modelo'] ?? ''}
              onChange={(e) => set('modelo', e.target.value)}
            />
          </Campo>
          <Campo etiqueta="Firmware">
            <Entrada
              value={campos['firmware'] ?? ''}
              onChange={(e) => set('firmware', e.target.value)}
            />
          </Campo>
        </div>

        <Campo etiqueta="Ubicación">
          <Entrada
            placeholder="Poste izquierdo, a 1 m"
            value={campos['ubicacion'] ?? ''}
            onChange={(e) => set('ubicacion', e.target.value)}
          />
        </Campo>

        {/* Solo donde hacen falta. Hoy únicamente la terminal facial: Kodica la
            llama por ISAPI para enrolar caras y para cargarle a dónde empujar
            sus eventos. */}
        {hablaPorIsapi && (
          <details className="rounded-lg border border-outline-variant p-4" open={!registro}>
            <summary className="cursor-pointer text-label-bold text-on-surface-variant">
              Credenciales del aparato
            </summary>
            <div className="mt-4 space-y-4">
              <Campo etiqueta="Usuario">
                <Entrada
                  placeholder="admin"
                  value={campos['usuarioAparato'] ?? ''}
                  onChange={(e) => set('usuarioAparato', e.target.value)}
                />
              </Campo>
              <Campo
                etiqueta="Contraseña"
                ayuda={
                  registro?.tienePassword
                    ? 'Hay una guardada. Escribe aquí solo para reemplazarla.'
                    : 'La del propio aparato. Se guarda y no se vuelve a mostrar.'
                }
              >
                <Entrada
                  type="password"
                  autoComplete="new-password"
                  value={campos['passwordAparato'] ?? ''}
                  onChange={(e) => set('passwordAparato', e.target.value)}
                />
              </Campo>
            </div>
          </details>
        )}

        {error && <ErrorCaja>{error}</ErrorCaja>}

        <div className="flex justify-end gap-2 pt-2">
          <Boton type="button" variante="texto" onClick={alCerrar}>
            Cancelar
          </Boton>
          <Boton type="submit" variante="primario" disabled={enviando}>
            {enviando ? 'Guardando…' : 'Guardar'}
          </Boton>
        </div>
      </form>
    </Modal>
  );
}

// ── Piezas compartidas ────────────────────────────────────────

function Volver({ a, children }: { a: string; children: React.ReactNode }) {
  const navegar = useNavigate();
  return (
    <div className="mb-stack-md">
      <Boton variante="texto" icono={Iconos.anterior} onClick={() => navegar(a)}>
        {children}
      </Boton>
    </div>
  );
}

function Seccion({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <Panel>
      <div className="border-b border-outline-variant px-5 py-3">
        <h2 className="text-label-bold uppercase tracking-wider text-on-surface-variant">
          {titulo}
        </h2>
      </div>
      {children}
    </Panel>
  );
}

function ListaAparatos({
  aparatos,
  enlace,
  vacio,
}: {
  aparatos: Dispositivo[];
  enlace: (d: Dispositivo) => string;
  vacio: string;
}) {
  if (aparatos.length === 0) return <Vacio>{vacio}</Vacio>;
  return (
    <ul className="divide-y divide-outline-variant">
      {aparatos.map((d) => (
        <li key={d.id}>
          <Link
            to={enlace(d)}
            className="toque flex items-center justify-between gap-4 px-5 py-4 transition-colors hover:bg-surface-container-high"
          >
            <div className="flex min-w-0 items-center gap-4">
              <span
                className={`rounded-full p-2 ${
                  d.vivo ? 'bg-success-container/40 text-success' : 'bg-error-container text-error'
                }`}
              >
                <Iconos.aparatos tam={20} />
              </span>
              <div className="min-w-0">
                <p className="truncate text-body-md text-on-surface">{d.nombre}</p>
                <p className="truncate text-label-sm text-on-surface-variant">
                  {d.tipo.replace(/_/g, ' ').toLowerCase()} ·{' '}
                  <span className="font-mono">{d.clienteId}</span>
                  {d.ip ? ` · ${d.ip}` : ''}
                </p>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <Chip tono={d.vivo ? 'ok' : 'malo'}>{d.vivo ? 'En línea' : 'Sin latido'}</Chip>
              <Iconos.siguiente tam={20} />
            </div>
          </Link>
        </li>
      ))}
    </ul>
  );
}

function Dato({
  etiqueta,
  valor,
  mono,
  ayuda,
}: {
  etiqueta: string;
  valor: string | null;
  mono?: boolean;
  ayuda?: string;
}) {
  return (
    <div className="flex items-baseline justify-between gap-4 px-5 py-3">
      <dt className="shrink-0 text-label-sm text-on-surface-variant">
        {etiqueta}
        {ayuda && <span className="block text-label-sm text-outline">{ayuda}</span>}
      </dt>
      <dd
        className={`min-w-0 truncate text-body-md ${valor ? 'text-on-surface' : 'text-outline'} ${
          mono ? 'font-mono' : ''
        }`}
      >
        {valor ?? '—'}
      </dd>
    </div>
  );
}

/**
 * El interruptor para ver lo dado de baja.
 *
 * **Solo aparece cuando hay algo que enseñar.** Un control permanente que casi
 * siempre no hace nada es ruido en una pantalla de configuración, y encima
 * sugiere que hay algo escondido cuando no lo hay. Con cero bajas, no existe.
 *
 * Y dice cuántas son: «ver 1 acceso dado de baja» informa, «ver dados de baja»
 * obliga a pulsarlo para averiguar si valía la pena.
 */
function InterruptorBajas({
  cuantas,
  puesto,
  alCambiar,
  que,
}: {
  cuantas: number;
  puesto: boolean;
  alCambiar: (v: boolean) => void;
  que: string;
}) {
  if (cuantas === 0) return null;
  const singular = que.replace(/e?s$/, '');
  return (
    <div className="mb-stack-md">
      <Boton variante="texto" onClick={() => alCambiar(!puesto)}>
        {puesto
          ? 'Ocultar los dados de baja'
          : `Ver ${cuantas} ${cuantas === 1 ? singular : que} dado${cuantas === 1 ? '' : 's'} de baja`}
      </Boton>
    </div>
  );
}
